import { useState, useEffect } from 'react'
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
  Stack,
} from '@mui/material'
import { Download, Refresh } from '@mui/icons-material'
import PrescriptionViewer from './PrescriptionViewer'
import { postSPDataWithParams } from 'src/supportPortal/services/services'
import { getDataMapFromCommonResponse, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { mediaURL } from 'src/consumer/services'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

export default function PrescriptionViewerWithFallback({ intervention }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showPDF, setShowPDF] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [apiFailed, setApiFailed] = useState(false)

  const bookedEventId = intervention?.bookedEventId
  const patientId = intervention?.patient?.patientId
  const bookedEventSlug = intervention?.bookedEventSlug

  useEffect(() => {
    // Reset state when intervention changes
    setShowPDF(false)
    setPdfUrl(null)
    setApiFailed(false)
    setError(null)
  }, [intervention])

  const downloadPrescriptionPDF = async () => {
    if (!bookedEventId) {
      toast.error('Booked event ID not available')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Try support portal prescription PDF generation API
      // Using POST method similar to mentor portal
      const response = await postSPDataWithParams(
        '/api/secure/cc/support/doctor/prescription/generate/pdf',
        null,
        {
          bookedEventId,
          'send-immediately': false,
        }
      )

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const data = getDataMapFromCommonResponse(response)
        const pdfMediaId = data?.pdfMediaId || data?.data?.pdfMediaId

        if (pdfMediaId) {
          const url = `${mediaURL}${pdfMediaId}`
          setPdfUrl(url)
          setShowPDF(true)
          setApiFailed(false)
        } else {
          throw new Error('PDF media ID not found in response')
        }
      } else {
        throw new Error('Failed to generate PDF')
      }
    } catch (err) {
      console.error('Error downloading prescription PDF:', err)
      setError('Failed to download prescription PDF. Please try again.')
      toast.error('Failed to download prescription PDF')
    } finally {
      setLoading(false)
    }
  }

  const handlePrescriptionViewerError = () => {
    // When PrescriptionViewer fails, trigger PDF download
    if (!apiFailed) {
      setApiFailed(true)
      downloadPrescriptionPDF()
    }
  }

  if (showPDF && pdfUrl) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            Prescription PDF
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setShowPDF(false)
                setPdfUrl(null)
                setApiFailed(false)
              }}
            >
              Try API View
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => {
                const link = document.createElement('a')
                link.href = pdfUrl
                link.download = `prescription_${intervention?.patient?.patientNumber || 'unknown'}_${dayjs().format('YYYY-MM-DD')}.pdf`
                link.target = '_blank'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
              }}
            >
              Download PDF
            </Button>
          </Stack>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <iframe
            src={pdfUrl}
            style={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              border: 'none',
            }}
            title="Prescription PDF"
          />
        </Box>
      </Box>
    )
  }

  if (apiFailed) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Unable to load prescription details via API.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Attempting to load prescription PDF instead...
          </Typography>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Generating PDF...</Typography>
            </Box>
          )}
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <PrescriptionViewer
        bookedEventId={bookedEventId}
        patientId={patientId}
        bookedEventSlug={bookedEventSlug}
        intervention={intervention}
        onError={handlePrescriptionViewerError}
      />
    </Box>
  )
}
